import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, CircleDot, Landmark, ArrowRight, Bookmark, Clock, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { getGuestId } from "../lib/guest";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ProjectWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  const load = () => {
    const gid = getGuestId();
    api.get(`/projects/${id}?guest_id=${gid}`).then((r) => setP(r.data)).catch(() => setErr(true));
  };
  useEffect(load, [id]);

  const advance = async (step) => {
    try {
      const { data } = await api.patch(`/projects/${id}/step`, {
        step_id: step.id, status: step.official ? "handed_off" : "done", guest_id: getGuestId(),
      });
      setP(data);
    } catch { toast.error("Could not update the step."); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-white/60" data-testid="project-not-found">Project not found. <button onClick={() => nav("/projects")} className="text-gold">Back to projects</button></div>;
  if (!p) return <div className="max-w-3xl mx-auto px-5 py-20 text-center text-white/50">Loading…</div>;

  const total = p.steps?.length || 0;
  const done = (p.steps || []).filter((s) => s.status === "done" || s.status === "handed_off").length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-10" data-testid="project-workspace">
      <button onClick={() => nav("/projects")} className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-1.5 mb-4"><ArrowLeft size={14} /> Projects</button>

      <div className="glass rounded-2xl p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{p.status}</span>
            <h1 className="font-display text-3xl text-white mt-2">{p.title}</h1>
            <p className="text-white/55 text-sm mt-1">{p.goal}</p>
          </div>
          <button onClick={() => nav("/ask", { state: { prompt: p.goal, projectId: p.id } })} data-testid="continue-with-ai"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gold text-ink text-sm font-semibold hover:brightness-110 transition-all">
            Continue with AI <ArrowRight size={15} />
          </button>
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-xs text-white/50 mb-1.5"><span>Progress</span><span>{done}/{total} · {pct}%</span></div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gold rounded-full transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-6">
        {/* Steps */}
        <div className="md:col-span-2 glass rounded-2xl p-6" data-testid="project-steps">
          <div className="text-sm font-semibold text-white/80 mb-4">Plan</div>
          <div className="space-y-1">
            {p.steps.map((s) => (
              <div key={s.id} data-testid="project-step"
                className={`flex items-center justify-between rounded-lg px-3 py-3 border ${s.official ? "border-gold/25 bg-gold/[0.04]" : "border-white/5 bg-white/[0.02]"}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {s.status === "done" ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                    : s.status === "handed_off" ? <Landmark size={18} className="text-gold shrink-0" />
                    : s.status === "active" ? <CircleDot size={18} className="text-gold shrink-0" />
                    : <Circle size={18} className="text-white/25 shrink-0" />}
                  <div className="min-w-0">
                    <div className={`text-sm truncate ${s.status === "pending" ? "text-white/50" : "text-white"}`}>{s.title}</div>
                    {s.official && <div className="text-[10px] text-gold/80 uppercase tracking-wider mt-0.5" data-testid="official-step">Official step — completed with Dubai Land Department</div>}
                  </div>
                </div>
                {s.status === "active" && (
                  <button onClick={() => advance(s)} data-testid="advance-step"
                    className="shrink-0 text-[11px] px-2.5 py-1.5 rounded-lg border border-gold/30 text-gold hover:bg-gold/10 transition-colors">
                    {s.official ? "Hand off to DLD" : "Mark done"}
                  </button>
                )}
                {s.status === "handed_off" && (
                  <a href="https://dubailand.gov.ae" target="_blank" rel="noreferrer" data-testid="dld-link"
                    className="shrink-0 text-[11px] px-2.5 py-1.5 rounded-lg border border-gold/30 text-gold hover:bg-gold/10 transition-colors inline-flex items-center gap-1">DLD <ArrowRight size={11} /></a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Saved items + timeline */}
        <div className="space-y-5">
          <div className="glass rounded-2xl p-6" data-testid="project-items">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3"><Bookmark size={15} className="text-gold" /> Saved items</div>
            {p.items?.length ? (
              <ul className="space-y-2">{p.items.map((it) => (
                <li key={it.id} className="text-xs text-white/70 border-l-2 border-gold/30 pl-3">{it.label}</li>
              ))}</ul>
            ) : <p className="text-xs text-white/45">Save comparables and options from the AI to keep them here.</p>}
          </div>

          <div className="glass rounded-2xl p-6" data-testid="project-timeline">
            <div className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3"><Clock size={15} className="text-gold" /> Timeline</div>
            <ul className="space-y-2.5">
              {[...(p.events || [])].reverse().map((e, i) => (
                <li key={i} className="text-xs text-white/60 flex gap-2"><span className="text-gold">•</span><span>{e.text}</span></li>
              ))}
            </ul>
          </div>

          {!user && (
            <div className="glass rounded-2xl p-6" data-testid="claim-prompt">
              <p className="text-xs text-white/60">This project is saved to this device. <button onClick={() => nav("/auth")} className="text-gold font-medium">Sign up</button> to keep it across devices — it'll be claimed to your account automatically.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
